/**
 * Coach claims — types, input schemas and wording shared by the rules
 * module, the server functions, the pages and the emails.
 *
 * A claim is a coach's membership of an organization (`coaches_ccos`) or a
 * completed training program (`coaches_ctps`). The organization that runs a
 * program decides its program claims.
 *
 * Aliases: claim verification, coach claims, verified by, credential badge,
 * membership verification, program verification, not ours
 */

import { z } from "zod";

import { ClaimVerificationState } from "~/lib/schemas/db-enums";

export const DCM_DECIDER_NAME = "Dream Coach Match";

export const MAX_CLAIM_NOTE_LENGTH = 2000;

export type ClaimKind = "membership" | "program";

/** One claim: the coach and the organization (membership) or program. */
export const claimRefSchema = z.object({
  kind: z.enum(["membership", "program"]),
  coachId: z.string().uuid(),
  /** `ccos.id` for a membership, `ctps.id` for a program. */
  targetId: z.string().uuid(),
});
export type ClaimRef = z.output<typeof claimRefSchema>;

export function claimKey(ref: ClaimRef): string {
  return `${ref.kind}:${ref.coachId}:${ref.targetId}`;
}

export type ClaimDecision =
  | ClaimVerificationState.Verified
  | ClaimVerificationState.NotConfirmed;

/** An ISO calendar day, `YYYY-MM-DD`. */
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const decisionFields = {
  decision: z.enum([
    ClaimVerificationState.Verified,
    ClaimVerificationState.NotConfirmed,
  ]),
  /** Only for a confirmation; null leaves the date out. */
  verificationDate: isoDay.nullable(),
  /** Only for "not confirmed". */
  note: z.string().trim().max(MAX_CLAIM_NOTE_LENGTH).nullable(),
};

export const claimDecisionInputSchema = z.object({
  claims: z.array(claimRefSchema).min(1).max(200),
  ...decisionFields,
});
export type ClaimDecisionInput = z.output<typeof claimDecisionInputSchema>;

/** A representative's decision, for the organization in the URL. */
export const organizationClaimDecisionInputSchema =
  claimDecisionInputSchema.extend({ slug: z.string().min(1) });

export const reviewRequestInputSchema = z.object({
  kind: z.enum(["membership", "program"]),
  targetId: z.string().uuid(),
  message: z.string().trim().max(MAX_CLAIM_NOTE_LENGTH).optional(),
});

/** Who decided a claim, for the badge and the lists. */
export type ClaimDecider =
  | { kind: "organization"; name: string }
  | { kind: "dcm" };

/** Who verified a credential on a public profile, and its month. */
export interface CredentialVerification {
  decider: ClaimDecider;
  date: string | null;
}

/** A claim as a representative sees it on "Coach claims". */
export interface OrganizationClaim extends ClaimRef {
  coachName: string;
  coachUsername: string;
  coachCity: string | null;
  /** The coach joined DCM through this organization's invite link. */
  joinedThroughInvite: boolean;
  /** "Member of [Organization]" or the program name. */
  label: string;
  /** Program claims only. */
  certificationDate: Date | null;
  addedAt: Date;
  state: ClaimVerificationState;
  decider: ClaimDecider | null;
  verificationDate: string | null;
  decisionNote: string | null;
}

export interface OrganizationClaims {
  organization: { id: string; name: string; slug: string };
  claims: OrganizationClaim[];
  /** The organization's programs, for the filter chips. */
  programs: { id: string; name: string }[];
}

/** The coach's own view of one claim on the qualifications page. */
export type CoachClaimStatus =
  | { kind: "verified"; decider: ClaimDecider; verificationDate: string | null }
  | { kind: "waiting"; organization: string }
  | { kind: "not_verified_yet" }
  | {
      kind: "not_confirmed";
      decider: ClaimDecider;
      note: string | null;
      reviewRequested: boolean;
    };

export interface CoachClaim {
  kind: ClaimKind;
  targetId: string;
  label: string;
  /** The organization the claim belongs to. */
  organization: string;
  certificationDate: Date | null;
  status: CoachClaimStatus;
}

/** A claim on the admin verifications page. */
export interface AdminClaim extends ClaimRef {
  coachName: string;
  coachEmail: string;
  coachUsername: string;
  /** The organization (membership) or program name and slug. */
  targetName: string;
  targetSlug: string | null;
  /** True when the target's public page exists (published, not a draft copy). */
  targetPublished: boolean;
  organization: string;
  state: ClaimVerificationState;
  decider: ClaimDecider | null;
  decidedAt: Date | null;
  verificationDate: string | null;
  decisionNote: string | null;
  reviewRequestedAt: Date | null;
  reviewMessage: string | null;
  certificationDate: Date | null;
  createdAt: Date;
}

export const adminClaimFiltersSchema = z.object({
  kind: z.enum(["membership", "program"]),
  targetId: z.string().uuid().optional(),
  /** The organization: its memberships, or the programs it runs. */
  ccoId: z.string().uuid().optional(),
  coachId: z.string().uuid().optional(),
  state: z
    .enum([
      ClaimVerificationState.Waiting,
      ClaimVerificationState.Verified,
      ClaimVerificationState.NotConfirmed,
    ])
    .optional(),
  reviewRequested: z.boolean().optional(),
  search: z.string().optional(),
});
export type AdminClaimFilters = z.output<typeof adminClaimFiltersSchema>;

export function deciderName(decider: ClaimDecider): string {
  return decider.kind === "dcm" ? DCM_DECIDER_NAME : decider.name;
}

/**
 * A decision's organization, or DCM when no organization is recorded. The
 * organization deciding a claim is always the one the claim belongs to.
 */
export function toDecider(
  decidedByCcoId: string | null,
  organization: string,
): ClaimDecider {
  return decidedByCcoId
    ? { kind: "organization", name: organization }
    : { kind: "dcm" };
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * Month and year of an ISO day, read from the string so no time zone shifts
 * it: "2026-10-01" → "Oct 2026", or "October 2026" with `long`.
 */
export function formatVerificationMonth(
  day: string,
  style: "short" | "long" = "short",
): string {
  const [year, month] = day.split("-");
  const name = MONTHS[Number(month) - 1] ?? "";
  return `${style === "long" ? name : name.slice(0, 3)} ${year}`.trim();
}

/** "Verified by [Organization] · Oct 2026", or without the date. */
export function verifiedByText(
  decider: ClaimDecider,
  verificationDate: string | null,
): string {
  const by = `Verified by ${deciderName(decider)}`;
  return verificationDate
    ? `${by} · ${formatVerificationMonth(verificationDate)}`
    : by;
}

/** Today as an ISO day in the viewer's time zone, for date inputs. */
export function todayIsoDay(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
